"use strict";
/**
 * @license
 * Copyright 2024 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use viewer file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * @file Helper functions to get the resolution of the viewer layers and panels.
 */
import { PerspectivePanel } from "#src/perspective_view/panel.js";
import { RenderedDataPanel } from "#src/rendered_data_panel.js";
import { RenderLayerRole } from "#src/renderlayer.js";
import { SliceViewPanel } from "#src/sliceview/panel.js";
import { ImageRenderLayer } from "#src/sliceview/volume/image_renderlayer.js";
import { SegmentationRenderLayer } from "#src/sliceview/volume/segmentation_renderlayer.js";
import { formatScaleWithUnitAsString } from "#src/util/si_units.js";
import { VolumeRenderingRenderLayer } from "#src/volume_rendering/volume_render_layer.js";
export function getViewerLayerResolutions(viewer) {
  function formatResolution(resolution, parentType) {
    if (resolution === void 0) return [];
    const resolutionStats = [];
    const {
      globalDimensionNames,
      displayDimensionUnits,
      displayDimensionIndices
    } = viewer.navigationState.displayDimensionRenderInfo.value;
    const firstDim = displayDimensionIndices[0];
    let singleScale = true;
    if (firstDim !== -1) {
      const unit = displayDimensionUnits[0];
      const factor = resolution[0];
      for (let i = 1; i < 3; ++i) {
        const dim = displayDimensionIndices[i];
        if (dim === -1) continue;
        if (displayDimensionUnits[i] !== unit || factor !== resolution[i]) {
          singleScale = false;
          break;
        }
      }
    }
    for (let i = 0; i < 3; ++i) {
      const dim = displayDimensionIndices[i];
      if (dim !== -1) {
        const dimensionName = globalDimensionNames[dim];
        if (i === 0 || !singleScale) {
          const formattedScale = formatScaleWithUnitAsString(
            resolution[i],
            displayDimensionUnits[i],
            { precision: 2, elide1: false }
          );
          resolutionStats.push({
            panelType: parentType,
            resolutionWithUnit: `${formattedScale}`,
            dimensionName: singleScale ? "All_" : dimensionName
          });
        }
      }
    }
    return resolutionStats;
  }
  const layers = viewer.layerManager.visibleRenderLayers;
  const map = /* @__PURE__ */ new Map();
  for (const layer of layers) {
    if (layer.role === RenderLayerRole.DATA) {
      let isVisble = false;
      const name = layer.userLayer.managedLayer.name;
      let type = "";
      let resolution;
      if (layer instanceof ImageRenderLayer) {
        type = "ImageRenderLayer";
        isVisble = layer.visibleSourcesList.length > 0;
        resolution = layer.highestResolutionLoadedVoxelSize;
      } else if (layer instanceof VolumeRenderingRenderLayer) {
        type = "VolumeRenderingRenderLayer";
        isVisble = layer.visibility.visible;
        resolution = layer.highestResolutionLoadedVoxelSize;
      } else if (layer instanceof SegmentationRenderLayer) {
        type = "SegmentationRenderLayer";
        isVisble = layer.visibleSourcesList.length > 0;
        resolution = layer.highestResolutionLoadedVoxelSize;
      }
      if (!isVisble) continue;
      map.set({ name, type }, formatResolution(resolution, type));
    }
  }
  return map;
}
export function getViewerPanelResolutions(panels, onlyUniqueResolutions = true) {
  function arePanelsEquivalent(panelResolution1, panelResolution2) {
    const physicalResolution1 = panelResolution1.physicalResolution;
    const physicalResolution2 = panelResolution2.physicalResolution;
    if (physicalResolution1.length !== physicalResolution2.length) {
      return false;
    }
    for (let i = 0; i < physicalResolution1.length; ++i) {
      const res1 = physicalResolution1[i];
      const res2 = physicalResolution2[i];
      if (res1.resolutionWithUnit !== res2.resolutionWithUnit || res1.panelType !== res2.panelType || res1.dimensionName !== res2.dimensionName) {
        return false;
      }
    }
    const pixelResolution1 = panelResolution1.pixelResolution;
    const pixelResolution2 = panelResolution2.pixelResolution;
    const width1 = Math.round(pixelResolution1.right - pixelResolution1.left);
    const width2 = Math.round(pixelResolution2.right - pixelResolution2.left);
    const height1 = Math.round(pixelResolution1.bottom - pixelResolution1.top);
    const height2 = Math.round(pixelResolution2.bottom - pixelResolution2.top);
    return width1 === width2 && height1 === height2;
  }
  const resolutions = [];
  for (const panel of panels) {
    if (!(panel instanceof RenderedDataPanel)) continue;
    const viewport = panel.renderViewport;
    const { width, height } = viewport;
    const panelLeft = panel.canvasRelativeClippedLeft;
    const panelTop = panel.canvasRelativeClippedTop;
    const panelRight = panelLeft + width;
    const panelBottom = panelTop + height;
    const {
      panelType,
      panelDimensionUnit
    } = determinePanelTypeAndUnit(panel);
    const panelResolution = {
      pixelResolution: {
        left: panelLeft,
        right: panelRight,
        top: panelTop,
        bottom: panelBottom,
        panelType
      },
      physicalResolution: []
    };
    const { physicalResolution } = panelResolution;
    const { navigationState } = panel;
    const {
      displayDimensionIndices,
      canonicalVoxelFactors,
      displayDimensionUnits,
      displayDimensionScales,
      globalDimensionNames
    } = navigationState.displayDimensionRenderInfo.value;
    const { factors } = navigationState.relativeDisplayScales.value;
    const zoom = navigationState.zoomFactor.value;
    const firstDim = displayDimensionIndices[0];
    let singleScale = true;
    if (firstDim !== -1) {
      const unit = displayDimensionUnits[0];
      const factor = factors[firstDim];
      for (let i = 1; i < 3; ++i) {
        const dim = displayDimensionIndices[i];
        if (dim === -1) continue;
        if (displayDimensionUnits[i] !== unit || factors[dim] !== factor) {
          singleScale = false;
          break;
        }
      }
    }
    for (let i = 0; i < 3; ++i) {
      const dim = displayDimensionIndices[i];
      if (dim !== -1) {
        const totalScale = displayDimensionScales[i] * zoom / canonicalVoxelFactors[i];
        const dimensionName = globalDimensionNames[dim];
        if (i === 0 || !singleScale) {
          const formattedScale = formatScaleWithUnitAsString(
            totalScale,
            displayDimensionUnits[i],
            { precision: 2, elide1: false }
          );
          physicalResolution.push({
            panelType,
            resolutionWithUnit: `${formattedScale}/${panelDimensionUnit}`,
            dimensionName: singleScale ? "All_" : dimensionName
          });
        }
      }
    }
    resolutions.push(panelResolution);
  }
  if (!onlyUniqueResolutions) {
    return resolutions;
  }
  const uniqueResolutions = [];
  for (const resolution of resolutions) {
    let found = false;
    for (const uniqueResolution of uniqueResolutions) {
      if (arePanelsEquivalent(resolution, uniqueResolution)) {
        found = true;
        break;
      }
    }
    if (!found) {
      uniqueResolutions.push(resolution);
    }
  }
  return uniqueResolutions;
}
function determinePanelTypeAndUnit(panel) {
  const isOrtographicProjection = panel instanceof PerspectivePanel && panel.viewer.orthographicProjection.value;
  const panelDimensionUnit = panel instanceof SliceViewPanel || isOrtographicProjection ? "px" : "vh";
  let panelType;
  if (panel instanceof SliceViewPanel) {
    panelType = "Slice view (2D)";
  } else if (isOrtographicProjection) {
    panelType = "Orthographic projection (3D)";
  } else if (panel instanceof PerspectivePanel) {
    panelType = "Perspective projection (3D)";
  } else {
    panelType = "Unknown";
  }
  return { panelType, panelDimensionUnit };
}
export function calculatePanelViewportBounds(panels) {
  const viewportBounds = {
    left: Number.POSITIVE_INFINITY,
    right: Number.NEGATIVE_INFINITY,
    top: Number.POSITIVE_INFINITY,
    bottom: Number.NEGATIVE_INFINITY,
    panelType: "All"
  };
  const allPanelViewports = [];
  for (const panel of panels) {
    if (!(panel instanceof RenderedDataPanel)) continue;
    const viewport = panel.renderViewport;
    const { width, height } = viewport;
    const panelLeft = panel.canvasRelativeClippedLeft;
    const panelTop = panel.canvasRelativeClippedTop;
    const panelRight = panelLeft + width;
    const panelBottom = panelTop + height;
    viewportBounds.left = Math.floor(Math.min(viewportBounds.left, panelLeft));
    viewportBounds.right = Math.ceil(
      Math.max(viewportBounds.right, panelRight)
    );
    viewportBounds.top = Math.ceil(Math.min(viewportBounds.top, panelTop));
    viewportBounds.bottom = Math.floor(
      Math.max(viewportBounds.bottom, panelBottom)
    );
    allPanelViewports.push({
      left: panelLeft,
      right: panelRight,
      top: panelTop,
      bottom: panelBottom,
      panelType: determinePanelTypeAndUnit(panel).panelType
    });
  }
  return {
    totalRenderPanelViewport: viewportBounds,
    individualRenderPanelViewports: allPanelViewports
  };
}
function formatPhysicalResolution(resolution) {
  if (resolution.length === 0) return null;
  const firstResolution = resolution[0];
  if (firstResolution.dimensionName === "All_") {
    return {
      type: firstResolution.panelType,
      resolution: firstResolution.resolutionWithUnit
    };
  } else {
    const resolutionText = resolution.map((res) => `${res.dimensionName} ${res.resolutionWithUnit}`).join(" ");
    return {
      type: firstResolution.panelType,
      resolution: resolutionText
    };
  }
}
function formatPixelResolution(panelArea) {
  const width = Math.round(panelArea.right - panelArea.left);
  const height = Math.round(panelArea.bottom - panelArea.top);
  const type = panelArea.panelType;
  return { width, height, type };
}
export function getViewerResolutionMetadata(viewer) {
  const panelResolution = getViewerPanelResolutions(viewer.display.panels);
  const panelResolutionData = [];
  for (const resolution of panelResolution) {
    const physicalResolution = formatPhysicalResolution(
      resolution.physicalResolution
    );
    if (physicalResolution === null) {
      continue;
    }
    const pixelResolution = formatPixelResolution(resolution.pixelResolution);
    panelResolutionData.push({
      type: physicalResolution.type,
      width: pixelResolution.width,
      height: pixelResolution.height,
      resolution: physicalResolution.resolution
    });
  }
  const layerResolution = getViewerLayerResolutions(viewer);
  const layerResolutionData = [];
  for (const [key, value] of layerResolution) {
    const { name, type } = key;
    if (type === "MultiscaleMeshLayer") {
      continue;
    }
    const physicalResolution = formatPhysicalResolution(value);
    if (physicalResolution === null) {
      continue;
    }
    layerResolutionData.push({
      name,
      type,
      resolution: physicalResolution.resolution
    });
  }
  return { panelResolutionData, layerResolutionData };
}
